import { useQuery } from "@tanstack/react-query";
import WheelGestures from "embla-carousel-wheel-gestures";
import { AwardIcon, ImageIcon, ShieldCheckIcon, TrashIcon } from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";

import type { CarouselApi } from "@/web/components/primitives/carousel";
import { Carousel, CarouselContent, CarouselItem } from "@/web/components/primitives/carousel";
import { Checkbox } from "@/web/components/primitives/checkbox";
import { Label } from "@/web/components/primitives/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/web/components/primitives/popover";
import { Spinner } from "@/web/components/primitives/spinner";
import { toaster } from "@/web/components/primitives/toast";
import { getDominantColorsHex, getTextColor } from "@/web/lib/colors";
import { ErrorHandler } from "@/web/lib/errors";
import { fetchPlaceDetailsByPlaceId } from "@/web/lib/googlePlaces/fetchPlaceFromPrediction";
import { getPlacePhotoUrls } from "@/web/lib/googlePlaces/photoUtils";
import {
  PlaceFormatters,
  placeDisplayName,
  placePrimaryTypeDisplayName,
} from "@/web/lib/googlePlaces/placeFormatters";
import { cn } from "@/web/lib/styling";
import { useProcedureMutation } from "@/web/lib/tanstack";
import { procedures } from "@tycho/web-bindings";
import { StandardFormWrapper } from "@/web/routes/_protected/smb/-onboarding/StandardFormWrapper";
import { isInvitedOnboarding, type SmbOnboarding } from "@/web/routes/_protected/smb/-types";

// ---------------------------------------------------------------------------
// Local types
// ---------------------------------------------------------------------------

type ColorOption = { primary: string; text: string };

type PhotoWithColors = {
  id: string;
  url: string;
  resourceName: string | null;
  colorOptions: ColorOption[];
};

export type PlaceDisplayInfo = {
  displayName: string;
  cityState: string;
  primaryTypeDisplayName: string;
  websiteUri: string;
  photos: { url: string; resourceName: string | null }[];
};

export type CarouselSelection = {
  selectedUrl: string | null;
  selectedColor: string;
  isCustomImage: boolean;
  uploadedFile: File | null;
};

const MAX_BACKGROUND_PHOTOS = 4;
const FALLBACK_COLOR = "#2D3B6E";

const CUSTOM_COLOR_PALETTE = [
  "#1E2A4A",
  "#2C4A7C",
  "#1F4E5F",
  "#1F5C3A",
  "#3E5C2C",
  "#5C4A1E",
  "#7A3B1E",
  "#8C2A2A",
  "#6B1E3F",
  "#4A2A5C",
  "#3A2E5C",
  "#2E2E2E",
];

// ---------------------------------------------------------------------------
// Place data
// ---------------------------------------------------------------------------

function usePlaceInfo(smbOnboarding: SmbOnboarding): {
  info: PlaceDisplayInfo;
  isLoading: boolean;
} {
  const googlePlaceId = smbOnboarding.googlePlaceId ?? null;

  const placeQuery = useQuery({
    queryKey: ["googlePlaceDetails", googlePlaceId, "customizeStyling"] as const,
    queryFn: () => {
      if (!googlePlaceId) throw new Error("googlePlaceId required");
      return fetchPlaceDetailsByPlaceId(googlePlaceId);
    },
    enabled: Boolean(googlePlaceId),
    staleTime: 5 * 60 * 1000,
  });

  const info = useMemo<PlaceDisplayInfo>(() => {
    const fallbackCityState = [smbOnboarding.addressParts?.city, smbOnboarding.addressParts?.state]
      .filter(Boolean)
      .join(", ");
    if (!placeQuery.data) {
      return {
        displayName: smbOnboarding.name ?? "Your Business",
        cityState: fallbackCityState,
        primaryTypeDisplayName: "Business",
        websiteUri: smbOnboarding.websiteUrl ?? "",
        photos: [],
      };
    }
    const place = placeQuery.data;
    return {
      displayName: placeDisplayName(place),
      cityState: PlaceFormatters.getCityState(place) ?? fallbackCityState,
      primaryTypeDisplayName: placePrimaryTypeDisplayName(place) ?? "Business",
      websiteUri: smbOnboarding.websiteUrl ?? "",
      photos: getPlacePhotoUrls(place, MAX_BACKGROUND_PHOTOS),
    };
  }, [
    placeQuery.data,
    smbOnboarding.name,
    smbOnboarding.addressParts?.city,
    smbOnboarding.addressParts?.state,
    smbOnboarding.websiteUrl,
  ]);

  return { info, isLoading: Boolean(googlePlaceId) && placeQuery.isPending };
}

async function extractColorOptions(imageUrl: string): Promise<ColorOption[]> {
  const colors = await getDominantColorsHex(imageUrl, {
    count: 4,
    maxLightness: 0.45,
    minDistance: 60,
  });
  return colors.map((primary) => ({ primary, text: getTextColor(primary) }));
}

// ---------------------------------------------------------------------------
// useCustomImageUpload — dropzone + base64 + palette extraction
// ---------------------------------------------------------------------------

type CustomImageState = {
  dataUrl: string | null;
  file: File | null;
  colorOptions: ColorOption[];
};

function useCustomImageUpload() {
  const [state, setState] = useState<CustomImageState>({
    dataUrl: null,
    file: null,
    colorOptions: [],
  });

  const handleUpload = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toaster({ message: "Please upload an image file", variant: "error" });
      return;
    }

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error("Failed to read image file"));
      reader.readAsDataURL(file);
    }).catch((error) => {
      ErrorHandler.default(error);
      return null;
    });
    if (!dataUrl) return;

    try {
      const colorOptions = await extractColorOptions(dataUrl);
      setState({ dataUrl, file, colorOptions });
    } catch (error) {
      ErrorHandler.default(error);
    }
  }, []);

  const reset = useCallback(() => setState({ dataUrl: null, file: null, colorOptions: [] }), []);

  return { ...state, handleUpload, reset };
}

// ---------------------------------------------------------------------------
// usePhotosWithColors — fetch dominant colors for each candidate photo
// ---------------------------------------------------------------------------

function usePhotosWithColors(photos: PlaceDisplayInfo["photos"]): {
  photos: PhotoWithColors[];
  isLoading: boolean;
} {
  const [state, setState] = useState<{ photos: PhotoWithColors[]; isLoading: boolean }>({
    photos: [],
    isLoading: true,
  });

  useEffect(() => {
    let cancelled = false;
    setState({ photos: [], isLoading: true });
    // `allSettled` never rejects, but the no-floating-promises lint rule needs a
    // terminal handler or `void`, so we prefix with `void`.
    void Promise.allSettled(
      photos.map(
        async (photo): Promise<PhotoWithColors> => ({
          id: photo.resourceName ?? photo.url,
          url: photo.url,
          resourceName: photo.resourceName,
          colorOptions: await extractColorOptions(photo.url),
        }),
      ),
    ).then((results) => {
      if (cancelled) return;
      // Keep photos whose color extraction succeeded; a single failure no longer
      // discards the rest. Failures are expected transients (CORS/image decode),
      // so log them for debugging without a user-facing toast — the UI already
      // communicates the all-empty case (custom-upload card for SMB onboarding,
      // bypass message for nominations).
      const resolved = results
        .filter((r): r is PromiseFulfilledResult<PhotoWithColors> => r.status === "fulfilled")
        .map((r) => r.value);
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length > 0) {
        ErrorHandler.logError(
          failed.length === 1
            ? failed[0]?.reason
            : new Error(`${failed.length} photos failed color extraction`),
        );
      }
      setState({ photos: resolved, isLoading: false });
    });
    return () => {
      cancelled = true;
    };
  }, [photos]);

  return state;
}

// ---------------------------------------------------------------------------
// useCardCarousel — carousel API + per-index color selection
// ---------------------------------------------------------------------------

function useCardCarousel(
  photos: PhotoWithColors[],
  customImageColorOptions: ColorOption[],
  allowCustomImage: boolean,
) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [selectedColors, setSelectedColors] = useState<Record<number, string>>({});

  useEffect(() => {
    setSelectedColors((prev) => {
      const next = { ...prev };
      photos.forEach((photo, index) => {
        if (!(index in next)) next[index] = photo.colorOptions[0]?.primary ?? FALLBACK_COLOR;
      });
      if (allowCustomImage && customImageColorOptions.length > 0) {
        const uploadIndex = photos.length;
        if (!(uploadIndex in next))
          next[uploadIndex] = customImageColorOptions[0]?.primary ?? FALLBACK_COLOR;
      }
      return next;
    });
  }, [photos, customImageColorOptions, allowCustomImage]);

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  const setSelectedColor = useCallback((index: number, color: string) => {
    setSelectedColors((prev) => ({ ...prev, [index]: color }));
  }, []);

  return { api, setApi, current, selectedColors, setSelectedColor };
}

// ---------------------------------------------------------------------------
// PreviewCard
// ---------------------------------------------------------------------------

function PreviewCard({
  place,
  imageUrl,
  selectedColor,
  isSelected = false,
  backgroundColorOverride,
  badge = { kind: "claimed" },
}: {
  place: PlaceDisplayInfo;
  imageUrl: string | null;
  selectedColor: string | undefined;
  isSelected?: boolean;
  backgroundColorOverride?: string;
  badge?: PreviewCardBadge;
}) {
  const primaryColor = backgroundColorOverride ?? selectedColor ?? FALLBACK_COLOR;
  const hasImage = imageUrl && imageUrl !== "none";

  return (
    <div
      className={cn(
        "relative flex h-100 w-75 flex-col overflow-hidden shadow-lg transition-all duration-300 ease-in-out",
        isSelected ? "scale-100 opacity-100" : "scale-90 cursor-pointer opacity-50",
      )}
      style={{ backgroundColor: primaryColor }}
    >
      {hasImage && (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${imageUrl})`, opacity: 0.4 }}
        />
      )}
      <div className="absolute inset-0 bg-linear-to-b from-black/16 via-black/8 to-transparent" />
      <div className="absolute inset-0 bg-linear-to-t from-black/60 via-transparent via-45% to-transparent" />
      <div className="relative z-10 flex h-full flex-col p-5 text-white">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-1 self-start bg-white/20 px-[8px] py-[6px] text-[11px] font-semibold tracking-widest uppercase">
            {badge.kind === "nominated" ? (
              <AwardIcon className="size-3" />
            ) : (
              <ShieldCheckIcon className="size-3" />
            )}
            {badge.kind === "nominated" ? "Nominated" : "Claimed"}
          </div>
          <div className="font-heading text-2xl uppercase">{place.displayName}</div>
          <div className="flex flex-wrap gap-1.5">
            {place.cityState && (
              <span className="border-2 border-white bg-transparent px-[8px] py-[6px] text-[11px] font-semibold tracking-widest uppercase">
                {place.cityState}
              </span>
            )}
            {place.primaryTypeDisplayName && (
              <span className="border-2 border-white bg-transparent px-[8px] py-[6px] text-[11px] font-semibold tracking-widest uppercase">
                {place.primaryTypeDisplayName}
              </span>
            )}
          </div>
        </div>
        <div className="mt-auto flex items-stretch divide-x divide-white/15">
          <div className="flex min-w-0 flex-col gap-2 overflow-hidden pr-4">
            <div className="text-[11px] font-semibold tracking-widest text-white/50 uppercase">
              Interested
            </div>
            <div className="withus-strong text-xs">0 people</div>
          </div>
          <div className="flex min-w-0 flex-col gap-2 overflow-hidden pl-4">
            <div className="text-[11px] font-semibold tracking-widest text-white/50 uppercase">
              Interest
            </div>
            <div className="withus-strong text-xs">$0</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DropzoneUpload
// ---------------------------------------------------------------------------

function DropzoneUpload({ onUpload }: { onUpload: (files: File[]) => void }) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onUpload,
    accept: { "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"], "image/gif": [".gif"] },
    multiple: false,
  });

  return (
    <div
      {...getRootProps()}
      className="flex h-10 w-full max-w-127 cursor-pointer items-center justify-center gap-2 border border-muted-foreground/40 text-sm font-bold hover:bg-muted/20"
    >
      <input {...getInputProps()} />
      <ImageIcon className="size-4" />
      {isDragActive ? "Drop here" : "Upload an image"}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CustomColorPickerButton — popover with a fixed 12-color palette
// ---------------------------------------------------------------------------

function CustomColorPickerButton({
  selectedColor,
  setSelectedColor,
}: {
  selectedColor: string | undefined;
  setSelectedColor: (color: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const isPaletteColor =
    selectedColor !== undefined && CUSTOM_COLOR_PALETTE.includes(selectedColor);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "size-8 cursor-pointer rounded-full border-2 border-transparent",
            isPaletteColor && "border-black",
          )}
          style={{
            background: isPaletteColor
              ? selectedColor
              : "conic-gradient(from 0deg, #8C2A2A, #7A3B1E, #5C4A1E, #3E5C2C, #1F5C3A, #1F4E5F, #2C4A7C, #1E2A4A, #3A2E5C, #4A2A5C, #6B1E3F, #8C2A2A)",
          }}
          title="Pick a custom color"
          aria-label="Pick a custom color"
        />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="center">
        <div className="grid grid-cols-6 gap-2">
          {CUSTOM_COLOR_PALETTE.map((color) => (
            <div
              key={color}
              className={cn(
                "inline-flex h-fit w-fit items-center justify-center rounded-full border-2 border-transparent p-0.5",
                color === selectedColor && "border-black",
              )}
            >
              <button
                type="button"
                onClick={() => {
                  setSelectedColor(color);
                  setOpen(false);
                }}
                className="size-7 cursor-pointer rounded-full"
                style={{ backgroundColor: color }}
                aria-label={`Select color ${color}`}
              />
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// ColorSelector
// ---------------------------------------------------------------------------

function ColorSelector({
  colorOptions,
  selectedColor,
  setSelectedColor,
  onReset,
  className,
}: {
  colorOptions: ColorOption[];
  selectedColor: string | undefined;
  setSelectedColor: (color: string) => void;
  onReset?: () => void;
  className?: string;
}) {
  return (
    <div className={cn("flex h-10 w-full items-center justify-center", className)}>
      <div className="flex items-center gap-2">
        {colorOptions.map((c) => (
          <div
            key={c.primary}
            className={cn(
              "inline-flex h-fit w-fit items-center justify-center rounded-full border-2 border-transparent p-0.5",
              c.primary === selectedColor && "border-black",
            )}
          >
            <button
              type="button"
              onClick={() => setSelectedColor(c.primary)}
              className="size-8 cursor-pointer rounded-full"
              style={{ backgroundColor: c.primary }}
            />
          </div>
        ))}
        <div className="h-6 w-px bg-primary/20" />
        <CustomColorPickerButton
          selectedColor={selectedColor}
          setSelectedColor={setSelectedColor}
        />
        {onReset && (
          <>
            <div className="h-6 w-px bg-primary/20" />
            <button
              type="button"
              onClick={onReset}
              className="flex size-8 items-center justify-center rounded-full border border-muted-foreground/20 hover:bg-muted"
              aria-label="Reset uploaded image"
            >
              <TrashIcon className="size-4 text-muted-foreground" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CardCustomizationCarousel
// ---------------------------------------------------------------------------

/** Status badge rendered on the preview card. Defaults to the SMB-onboarding
 * "Claimed" badge; the nomination flow passes `{ kind: "nominated" }` so its
 * preview matches the nomination browse card. */
export type PreviewCardBadge = { kind: "claimed" | "nominated" };

export function CardCustomizationCarousel({
  place,
  placeIsLoading,
  onChange,
  allowCustomImage = true,
  badge = { kind: "claimed" },
  onPhotosResolved,
}: {
  place: PlaceDisplayInfo;
  placeIsLoading: boolean;
  onChange?: (state: CarouselSelection) => void;
  allowCustomImage?: boolean;
  badge?: PreviewCardBadge;
  /**
   * Reports the resolved photo set once color extraction settles. `undefined`
   * means "still loading"; a number is the count of photos that yielded a
   * palette (0 means every photo's color extraction failed). Consumers that
   * disable custom uploads use this to detect the degenerate "no usable
   * image" state instead of getting stuck behind an unrecoverable gate.
   */
  onPhotosResolved?: (available: number | undefined) => void;
}) {
  const { photos, isLoading: isLoadingColors } = usePhotosWithColors(place.photos);
  const customImage = useCustomImageUpload();
  const { api, setApi, current, selectedColors, setSelectedColor } = useCardCarousel(
    photos,
    customImage.colorOptions,
    allowCustomImage,
  );

  useEffect(() => {
    if (!onChange) return;
    const totalPhotos = photos.length;
    const isUploadCardSelected = allowCustomImage && current === totalPhotos;
    const selectedPhoto = photos[current] ?? null;
    onChange({
      selectedUrl: isUploadCardSelected ? customImage.dataUrl : (selectedPhoto?.url ?? null),
      selectedColor: selectedColors[current] ?? FALLBACK_COLOR,
      isCustomImage: isUploadCardSelected,
      uploadedFile: isUploadCardSelected ? customImage.file : null,
    });
  }, [
    allowCustomImage,
    current,
    customImage.dataUrl,
    customImage.file,
    onChange,
    photos,
    selectedColors,
  ]);

  useEffect(() => {
    // Reflect the resolved-photo count to the consumer so a caller that gates
    // submit on a selection (e.g. the nomination flow, which hides the custom-
    // upload card) can detect a total color-extraction failure and bypass the
    // gate instead of trapping the user.
    if (isLoadingColors) {
      onPhotosResolved?.(undefined);
    } else {
      onPhotosResolved?.(photos.length);
    }
  }, [onPhotosResolved, photos, isLoadingColors]);

  if (placeIsLoading || isLoadingColors) {
    return (
      <div className="flex h-120 w-full items-center justify-center">
        <Spinner className="size-10" />
      </div>
    );
  }

  const totalPhotos = photos.length;
  const isUploadCardSelected = allowCustomImage && current === totalPhotos;
  const currentPhoto = photos[current];
  const currentPhotoColorOptions = isUploadCardSelected
    ? customImage.colorOptions
    : (currentPhoto?.colorOptions ?? []);
  const currentPhotoSelectedColor = selectedColors[current];

  if (totalPhotos === 0 && !allowCustomImage) {
    return (
      <div className="flex h-120 w-full items-center justify-center text-center text-muted-foreground">
        No images available. Please try again.
      </div>
    );
  }

  return (
    <div className="relative flex w-full flex-col items-center justify-center gap-4 overflow-hidden">
      <Carousel
        setApi={setApi}
        plugins={[WheelGestures({ forceWheelAxis: "x", wheelDraggingClass: "is-wheel-dragging" })]}
        opts={{ align: "start" }}
        className="w-75 overflow-visible"
      >
        <CarouselContent viewportClassName="overflow-visible">
          {photos.map((photo, index) => (
            <CarouselItem key={photo.id} onClick={() => api?.scrollTo(index)}>
              <PreviewCard
                place={place}
                imageUrl={photo.url}
                selectedColor={selectedColors[index]}
                isSelected={index === current}
                badge={badge}
              />
            </CarouselItem>
          ))}
          {allowCustomImage && (
            <CarouselItem onClick={() => api?.scrollTo(totalPhotos)}>
              <PreviewCard
                place={place}
                imageUrl={customImage.dataUrl}
                selectedColor={selectedColors[totalPhotos]}
                isSelected={isUploadCardSelected}
                backgroundColorOverride={customImage.dataUrl ? undefined : "#111111"}
                badge={badge}
              />
            </CarouselItem>
          )}
        </CarouselContent>
      </Carousel>

      <div className="flex items-center justify-center gap-2.5">
        {photos.map((photo, index) => (
          <button
            key={`dot-${photo.id}`}
            type="button"
            className={cn(
              "h-2 w-2 rounded-full transition-all duration-300 ease-in-out",
              index === current ? "scale-100 bg-primary" : "scale-80 bg-primary/30",
            )}
            onClick={() => api?.scrollTo(index)}
            aria-label={`Go to photo ${index + 1}`}
          />
        ))}
        {allowCustomImage && (
          <button
            type="button"
            className={cn(
              "h-2 w-2 rounded-full transition-all duration-300 ease-in-out",
              isUploadCardSelected ? "scale-100 bg-primary" : "scale-80 bg-primary/30",
            )}
            onClick={() => api?.scrollTo(totalPhotos)}
            aria-label="Go to custom image"
          />
        )}
      </div>

      {isUploadCardSelected ? (
        customImage.dataUrl ? (
          <ColorSelector
            colorOptions={currentPhotoColorOptions}
            selectedColor={currentPhotoSelectedColor}
            setSelectedColor={(color) => setSelectedColor(totalPhotos, color)}
            onReset={customImage.reset}
            className="h-auto p-4"
          />
        ) : (
          <div className="flex h-10 w-full items-center justify-center px-6">
            <DropzoneUpload onUpload={customImage.handleUpload} />
          </div>
        )
      ) : (
        <ColorSelector
          colorOptions={currentPhotoColorOptions}
          selectedColor={currentPhotoSelectedColor}
          setSelectedColor={(color) => setSelectedColor(current, color)}
        />
      )}

      <div className="absolute right-0 bottom-0 z-30 h-full w-5 bg-linear-to-l from-background to-transparent" />
      <div className="absolute bottom-0 left-0 z-30 h-full w-5 bg-linear-to-r from-background to-transparent" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload pipeline: load image blob (from URL or File) → PUT to signed URL → finalize
// ---------------------------------------------------------------------------

async function resolveImageBlob(args: {
  isCustomImage: boolean;
  uploadedFile: File | null;
  selectedUrl: string | null;
}): Promise<{ blob: Blob; contentType: string }> {
  if (args.isCustomImage && args.uploadedFile) {
    return { blob: args.uploadedFile, contentType: args.uploadedFile.type || "image/jpeg" };
  }
  if (!args.selectedUrl) throw new Error("No image selected");
  const response = await fetch(args.selectedUrl);
  if (!response.ok) throw new Error(`Failed to fetch image (${response.status})`);
  const blob = await response.blob();
  return { blob, contentType: blob.type || "image/jpeg" };
}

async function putToSignedUrl(
  uploadUrl: string,
  blob: Blob,
  contentType: string,
  contentLengthRange: string,
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "content-type": contentType,
      "x-goog-content-length-range": contentLengthRange,
    },
    body: blob,
  });
  if (!response.ok) {
    const text = await response.text().catch((e: unknown) => {
      ErrorHandler.reportError(e);
      return "";
    });
    throw new Error(`GCS upload failed (${response.status}): ${text}`);
  }
}

// ---------------------------------------------------------------------------
// CustomizingStyle
// ---------------------------------------------------------------------------

export function CustomizingStyle({
  smbOnboarding,
  onBack,
  onContinue,
}: {
  smbOnboarding: SmbOnboarding;
  /** Undefined hides the top-nav back button (invited flow's first step has no prior step). */
  onBack?: () => void;
  onContinue: () => void;
}) {
  const [carouselSelection, setCarouselSelection] = useState<CarouselSelection | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Whether the shareable card minted at the end of onboarding shows on the
  // public `/browse` page. Defaults to `true`; seeded from the saved row so a
  // resume/back-nav reflects the prior choice (`browsable` is non-optional in
  // the v2 onboarding row, defaulting to `true`).
  const browsableId = useId();
  const [browsable, setBrowsable] = useState(smbOnboarding.browsable ?? true);

  const { info: place, isLoading: placeIsLoading } = usePlaceInfo(smbOnboarding);
  const { mutateAsync: createHeroUploadUrl } = useProcedureMutation(procedures.createHeroUploadUrl);
  const { mutateAsync: finalizeHeroAndCustomizeStyling } = useProcedureMutation(
    procedures.finalizeHeroAndCustomizeStyling,
  );

  const handleSubmit = async (): Promise<boolean> => {
    if (!carouselSelection) {
      toaster({ message: "Please pick an image before continuing", variant: "error" });
      return false;
    }

    const { uploadedFile, isCustomImage, selectedUrl, selectedColor } = carouselSelection;
    if (isCustomImage && !uploadedFile) {
      toaster({ message: "Please upload an image first", variant: "error" });
      return false;
    }
    if (!isCustomImage && !selectedUrl) {
      toaster({ message: "No image available to upload", variant: "error" });
      return false;
    }

    setIsSubmitting(true);
    try {
      const { blob, contentType } = await resolveImageBlob({
        isCustomImage,
        uploadedFile,
        selectedUrl,
      });

      const { uploadUrl, objectPath, contentLengthRange } = await createHeroUploadUrl({
        contentType,
      });
      await putToSignedUrl(uploadUrl, blob, contentType, contentLengthRange);

      const primary = selectedColor;
      const text = getTextColor(primary);
      await finalizeHeroAndCustomizeStyling({
        objectPath,
        colorPrimary: primary,
        colorSecondary: "#444444",
        colorTertiary: "#999999",
        colorText: text,
        browsable,
      });
      return true;
    } catch (error) {
      ErrorHandler.default(error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <StandardFormWrapper
      backAction={onBack}
      bottomContinueButton
      continueAction={() => {
        handleSubmit()
          .then((didSubmit) => {
            if (didSubmit) onContinue();
          })
          .catch(ErrorHandler.default);
      }}
      isSubmitting={isSubmitting}
      progress={isInvitedOnboarding(smbOnboarding) ? 20 : 57.14}
      childrenParentClassName="max-w-full gap-[2.11vh] md:gap-[4.21vh]"
      title="Customize your business card"
    >
      <CardCustomizationCarousel
        place={place}
        placeIsLoading={placeIsLoading}
        onChange={setCarouselSelection}
      />
      <div className="flex w-full max-w-127 items-start gap-3 self-center">
        <Checkbox
          id={browsableId}
          checked={browsable}
          onCheckedChange={(next) => setBrowsable(next === true)}
          disabled={isSubmitting}
          className="mt-0.5"
        />
        <Label htmlFor={browsableId} className="cursor-pointer text-sm leading-snug font-normal">
          Show my card on the public browse page so people can discover my business. You can change
          this anytime from your dashboard.
        </Label>
      </div>
    </StandardFormWrapper>
  );
}
